/**
 * NhlEdgeStatsSection
 *
 * Affiche les statistiques avancées NHL Edge™ pour un joueur :
 *   - Patineur : vitesse de patinage, vitesse de tir, distance, temps de zone, tirs par zone.
 *   - Gardien  : moy. buts alloués, matchs >  .900, différentiel de buts, tirs par zone.
 *
 * Les données proviennent des endpoints /v1/edge/skater-detail et /v1/edge/goalie-detail
 * via le proxy Supabase `nhl-data`.
 */

import { useEffect, useState } from 'react';
import { translate } from '../../lib/translations';
import {
  fetchNhlEdgeSkaterDetail,
  fetchNhlEdgeGoalieDetail,
  NhlEdgeSkaterDetail,
  NhlEdgeGoalieDetail,
  NhlEdgeError,
  rankToPercentile,
  percentileToColorClass,
} from '../../lib/api/nhlEdgeStats';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Language = 'fr' | 'en';
type GameType = 2 | 3;

interface NhlEdgeStatsSectionProps {
  playerId: number;
  isGoalie: boolean;
  language: Language;
}

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------

export function NhlEdgeStatsSection({ playerId, isGoalie, language }: NhlEdgeStatsSectionProps) {
  const [gameType, setGameType] = useState<GameType>(2);
  const [season, setSeason] = useState<string>('20252026');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [skaterData, setSkaterData] = useState<NhlEdgeSkaterDetail | null>(null);
  const [goalieData, setGoalieData] = useState<NhlEdgeGoalieDetail | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setIsEmpty(false);
    setSkaterData(null);
    setGoalieData(null);

    const seasonArg = season === 'now' ? undefined : season;
    const fetchData = isGoalie
      ? fetchNhlEdgeGoalieDetail(playerId, seasonArg, gameType)
      : fetchNhlEdgeSkaterDetail(playerId, seasonArg, gameType);

    fetchData
      .then((data) => {
        if (cancelled) return;

        const hasData = data != null && typeof data === 'object' && Object.keys(data).length > 0;
        if (!hasData) {
          setIsEmpty(true);
        } else if (isGoalie) {
          setGoalieData(data as NhlEdgeGoalieDetail);
        } else {
          setSkaterData(data as NhlEdgeSkaterDetail);
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof NhlEdgeError
            ? err.message
            : translate('nhl.edge.error.unavailable', language);
        setError(msg);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [playerId, isGoalie, gameType, season, language]);

  return (
    <section className="card shadow-sm nhl-edge-section">
      {/* -------- En-tête -------- */}
      <div
        className="card-header bg-transparent"
        style={{
          borderBottom: '1px solid rgba(59,130,246,0.25)',
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-2">
            <div>
              <h2 className="h5 mb-0 d-flex align-items-center gap-2">
                {translate('nhl.edge.title', language)}
                <span
                  className="badge ms-1"
                  style={{
                    background: 'linear-gradient(90deg, #0ea5e9, #6366f1)',
                    fontSize: '0.65rem',
                    letterSpacing: '0.05em',
                    verticalAlign: 'middle',
                  }}
                >
                  EDGE™
                </span>
              </h2>
              <p className="text-muted small mb-0">{translate('nhl.edge.subtitle', language)}</p>
            </div>
          </div>

          {/* Sélecteur saison régulière / éliminatoires */}
          <div
            className="btn-group btn-group-sm"
            role="group"
            aria-label={translate('nhl.edge.game.type.regular', language)}
          >
            <button
              type="button"
              className={`btn ${gameType === 2 ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setGameType(2)}
            >
              {translate('nhl.edge.game.type.regular', language)}
            </button>
            <button
              type="button"
              className={`btn ${gameType === 3 ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setGameType(3)}
            >
              {translate('nhl.edge.game.type.playoffs', language)}
            </button>
          </div>
          {/* Sélecteur de saison */}
          <div className="ms-2">
            <select
              className="form-select form-select-sm"
              style={{ width: 130 }}
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              aria-label={translate('nhl.edge.season.select', language) || 'Season'}
            >
              <option value="20252026">2025/2026</option>
              <option value="20242025">2024/2025</option>
              <option value="20232024">2023/2024</option>
            </select>
          </div>
        </div>
      </div>

      {/* -------- Corps -------- */}
      <div className="card-body">
        {loading && <LoadingState language={language} />}

        {!loading && error && <ErrorState message={error} />}

        {!loading && !error && isEmpty && (
          <EmptyState language={language} isGoalie={isGoalie} />
        )}

        {!loading && !error && !isEmpty && !isGoalie && skaterData && (
          <SkaterEdgeContent data={skaterData} language={language} />
        )}

        {!loading && !error && !isEmpty && isGoalie && goalieData && (
          <GoalieEdgeContent data={goalieData} language={language} />
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// États (loading / error / empty)
// ---------------------------------------------------------------------------

function LoadingState({ language }: { language: Language }) {
  return (
    <div className="d-flex align-items-center justify-content-center gap-2 py-4 text-muted">
      <div className="spinner-border spinner-border-sm text-primary" role="status" />
      <span>{translate('nhl.edge.loading', language)}</span>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="alert alert-warning mb-0" role="alert">
      <span>{message}</span>
    </div>
  );
}

function EmptyState({ language, isGoalie }: { language: Language; isGoalie: boolean }) {
  const msg = isGoalie
    ? translate('nhl.edge.no.goalie.data', language)
    : translate('nhl.edge.error.no.data', language);
  return (
    <div className="text-muted text-center py-4">
      <p className="mb-0">{msg}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contenu patineur
// ---------------------------------------------------------------------------

function SkaterEdgeContent({
  data,
  language,
}: {
  data: NhlEdgeSkaterDetail;
  language: Language;
}) {
  const skatingSpeed = data.skatingSpeed?.playerSeason;
  const shotSpeed = data.topShotSpeed?.playerSeason;
  const distance = data.skatingSummary?.playerSeason;
  const zone = data.zoneTime?.playerSeason;
  const sog = data.sogSummary?.playerSeason;

  return (
    <div className="d-flex flex-column gap-4">
      {/* ---- Rangée 1 : Vitesses & Distance ---- */}
      <div className="row g-3">
        {skatingSpeed != null && (
          <div className="col-12 col-sm-6 col-xl-4">
            <SpeedMetricCard
              title={translate('nhl.edge.skating.speed.title', language)}
              value={skatingSpeed.maxSpeed}
              unit={translate('nhl.edge.skating.speed.unit', language)}
              rank={skatingSpeed.rank}
              total={skatingSpeed.total}
              language={language}
              accentColor="#0ea5e9"
              extraStats={[
                skatingSpeed.burstsOver22 != null
                  ? {
                      label: translate('nhl.edge.skating.speed.bursts22', language),
                      value: skatingSpeed.burstsOver22,
                    }
                  : null,
                skatingSpeed.burstsOver20 != null
                  ? {
                      label: translate('nhl.edge.skating.speed.bursts20', language),
                      value: skatingSpeed.burstsOver20,
                    }
                  : null,
              ].filter(Boolean) as Array<{ label: string; value: number }>}
            />
          </div>
        )}

        {shotSpeed != null && (
          <div className="col-12 col-sm-6 col-xl-4">
            <SpeedMetricCard
              title={translate('nhl.edge.shot.speed.title', language)}
              value={shotSpeed.maxSpeed}
              unit={translate('nhl.edge.shot.speed.unit', language)}
              rank={shotSpeed.rank}
              total={shotSpeed.total}
              language={language}
              accentColor="#f59e0b"
            />
          </div>
        )}

        {distance != null && (
          <div className="col-12 col-sm-6 col-xl-4">
            <DistanceCard data={distance} language={language} />
          </div>
        )}
      </div>

      {/* ---- Rangée 2 : Temps en zone & Tirs ---- */}
      <div className="row g-3">
        {zone != null && (
          <div className="col-12 col-lg-6">
            <ZoneTimeCard data={zone} language={language} />
          </div>
        )}

        {sog != null && (
          <div className="col-12 col-lg-6">
            <ShotZoneCard data={sog} language={language} />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contenu gardien
// ---------------------------------------------------------------------------

function GoalieEdgeContent({
  data,
  language,
}: {
  data: NhlEdgeGoalieDetail;
  language: Language;
}) {
  // Supporte deux formes de réponse :
  // 1) l'ancien shape interne { gaa: { playerSeason: { value, rank, total } }, ... }
  // 2) le JSON direct de l'API NHL Edge { stats: { goalsAgainstAvg: { value, percentile }, ... }, shotLocationDetails: [...] }
  function statToPlayerSeason(stat?: { value?: number; percentile?: number } | null) {
    if (!stat || stat.value == null) return undefined;
    const pct = typeof stat.percentile === 'number' ? stat.percentile : 0;
    const rank = Math.max(1, Math.round((1 - pct) * 100));
    return { value: stat.value, rank, total: 100 } as { value: number; rank: number; total: number };
  }

  const gaa = data.gaa?.playerSeason ?? statToPlayerSeason((data as any).stats?.goalsAgainstAvg);
  const gamesAbove = data.gamesAbove900?.playerSeason ?? statToPlayerSeason((data as any).stats?.gamesAbove900);
  const goalDiff = data.goalDifferentialPer60?.playerSeason ?? statToPlayerSeason((data as any).stats?.goalDifferentialPer60);
  const support = data.goalSupport?.playerSeason ?? statToPlayerSeason((data as any).stats?.goalSupportAvg ?? (data as any).stats?.goalSupport);
  const pointPctg = data.pointPctg?.playerSeason ?? statToPlayerSeason((data as any).stats?.pointPctg);

  // Si aucune donnée n'est disponible, afficher un état vide
  const hasAny =
    gaa != null ||
    gamesAbove != null ||
    goalDiff != null ||
    support != null ||
    pointPctg != null;

  if (!hasAny) {
    return <EmptyState language={language} isGoalie />;
  }

  return (
    <div className="row g-3">
      {gaa != null && (
        <div className="col-12 col-sm-6 col-xl-4">
          <GoalieMetricCard
            title={translate('nhl.edge.goalie.gaa.title', language)}
            value={formatGoalieValue(gaa.value, 2)}
            rank={gaa.rank}
            total={gaa.total}
            language={language}
            accentColor="#6366f1"
            
          />
        </div>
      )}

      {gamesAbove != null && (
        <div className="col-12 col-sm-6 col-xl-4">
          <GoalieMetricCard
            title={translate('nhl.edge.goalie.games.above.900', language)}
            value={
              gamesAbove.value != null ? `${(gamesAbove.value * 100).toFixed(1)}%` : '—'
            }
            rank={gamesAbove.rank}
            total={gamesAbove.total}
            language={language}
            accentColor="#10b981"
          />
        </div>
      )}

      {goalDiff != null && (
        <div className="col-12 col-sm-6 col-xl-4">
          <GoalieMetricCard
            title={translate('nhl.edge.goalie.goal.differential', language)}
            value={formatGoalieValue(goalDiff.value, 2)}
            rank={goalDiff.rank}
            total={goalDiff.total}
            language={language}
            accentColor="#0ea5e9"
          />
        </div>
      )}

      {support != null && (
        <div className="col-12 col-sm-6 col-xl-4">
          <GoalieMetricCard
            title={translate('nhl.edge.goalie.goal.support', language)}
            value={formatGoalieValue(support.value, 2)}
            rank={support.rank}
            total={support.total}
            language={language}
            accentColor="#f59e0b"
          />
        </div>
      )}

      {pointPctg != null && (
        <div className="col-12 col-sm-6 col-xl-4">
          <GoalieMetricCard
            title={translate('nhl.edge.goalie.point.pctg', language)}
            value={
              pointPctg.value != null ? `${(pointPctg.value * 100).toFixed(1)}%` : '—'
            }
            rank={pointPctg.rank}
            total={pointPctg.total}
            language={language}
            accentColor="#a855f7"
          />
        </div>
      )}

      {/* Shot location breakdown - supporte shotLocation.details ou shotLocationDetails (API) */}
      {((data as any).shotLocation?.details && (data as any).shotLocation.details.length > 0) ||
      ((data as any).shotLocationDetails && (data as any).shotLocationDetails.length > 0) ? (
        <div className="col-12">
          <GoalieShotLocationCard
            data={(data as any).shotLocation?.details ?? (data as any).shotLocationDetails}
            language={language}
          />
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Carte de métrique vitesse (patineur)
// ---------------------------------------------------------------------------

interface SpeedMetricCardProps {
  title: string;
  value?: number;
  unit: string;
  rank?: number;
  total?: number;
  language: Language;
  accentColor: string;
  extraStats?: Array<{ label: string; value: number }>;
}

function SpeedMetricCard({
  title,
  value,
  unit,
  rank,
  total,
  language,
  accentColor,
  extraStats,
}: SpeedMetricCardProps) {
  const percentile = rankToPercentile(rank, total);
  const colorClass = percentileToColorClass(percentile);

  return (
    <div
      className="h-100 rounded-3 p-3"
      style={{
        background: 'var(--card-bg)',
        border: `1px solid ${accentColor}33`,
        borderTop: `3px solid ${accentColor}`,
      }}
    >
      <div className="mb-3">
        <span className="text-muted small fw-semibold text-uppercase">{title}</span>
      </div>

      <div className="mb-3">
        <span className="display-6 fw-bold">
          {value != null ? value.toFixed(1) : '—'}
        </span>
        <span className="text-muted ms-1 small">{unit}</span>
      </div>

      {rank != null && total != null && (
        <RankIndicator percentile={percentile} colorClass={colorClass} language={language} />
      )}

      {extraStats && extraStats.length > 0 && (
        <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          {extraStats.map((stat, i) => (
            <div key={i} className="d-flex justify-content-between align-items-center small text-muted mb-1">
              <span>{stat.label}</span>
              <span className="fw-semibold">{stat.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Carte de distance
// ---------------------------------------------------------------------------

function DistanceCard({
  data,
  language,
}: {
  data: NonNullable<NhlEdgeSkaterDetail['skatingSummary']>['playerSeason'] & object;
  language: Language;
}) {
  const percentile = rankToPercentile(data.rank, data.total);
  const colorClass = percentileToColorClass(percentile);
  const distanceKm =
    data.totalDistance != null ? (data.totalDistance / 1000).toFixed(1) : null;
  const avgKm =
    data.avgDistance != null ? (data.avgDistance / 1000).toFixed(2) : null;

  return (
    <div
      className="h-100 rounded-3 p-3"
      style={{
        background: 'var(--card-bg)',
        border: '1px solid rgba(16,185,129,0.2)',
        borderTop: '3px solid #10b981',
      }}
    >
      <div className="mb-3">
        <span className="text-muted small fw-semibold text-uppercase">
          {translate('nhl.edge.distance.title', language)}
        </span>
      </div>

      <div className="mb-1">
        <span className="display-6 fw-bold">{distanceKm ?? '—'}</span>
        <span className="text-muted ms-1 small">
          {translate('nhl.edge.distance.unit', language)}
        </span>
      </div>
      <div className="text-muted small mb-3">
        {translate('nhl.edge.distance.total', language)}
      </div>

      {avgKm != null && (
        <div className="text-muted small mb-3">
          {translate('nhl.edge.distance.avg', language)}{' '}
          <strong>{avgKm} {translate('nhl.edge.distance.unit', language)}</strong>
        </div>
      )}

      {data.rank != null && data.total != null && (
        <RankIndicator
          percentile={percentile}
          colorClass={colorClass}
          language={language}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Indicateur de rang
// ---------------------------------------------------------------------------

interface RankIndicatorProps {
  percentile: number | null;
  colorClass: string;
  language: Language;
}

function RankIndicator({ percentile, colorClass, language }: RankIndicatorProps) {
  const barWidth = percentile != null ? Math.max(2, percentile) : 0;

  return (
    <div>
      {percentile != null && (
        <div className="d-flex justify-content-between align-items-center small mb-1">
          <span className={`fw-bold ${colorClass}`}>
            {translate('nhl.edge.percentile', language)} {percentile}%
          </span>
        </div>
      )}

      {percentile != null && (
        <div
          className="rounded-pill overflow-hidden"
          style={{ height: 6, background: 'var(--bs-gray-700, rgba(148,163,184,0.2))' }}
        >
          <div
            className="h-100 rounded-pill"
            style={{
              width: `${barWidth}%`,
              background: percentileGradient(percentile),
              transition: 'width 0.6s ease',
            }}
          />
        </div>
      )}
    </div>
  );
}

function percentileGradient(percentile: number): string {
  if (percentile >= 90) return 'linear-gradient(90deg, #10b981, #06d6a0)';
  if (percentile >= 70) return 'linear-gradient(90deg, #0ea5e9, #38bdf8)';
  if (percentile >= 50) return 'linear-gradient(90deg, #3b82f6, #818cf8)';
  if (percentile >= 30) return 'linear-gradient(90deg, #f59e0b, #fbbf24)';
  return 'linear-gradient(90deg, #f43f5e, #fb7185)';
}

// ---------------------------------------------------------------------------
// Carte de temps en zone
// ---------------------------------------------------------------------------

type ZoneData = NonNullable<NhlEdgeSkaterDetail['zoneTime']>['playerSeason'] & object;

function ZoneTimeCard({ data, language }: { data: ZoneData; language: Language }) {
  const off = data.offensiveZonePctg ?? 0;
  const neu = data.neutralZonePctg ?? 0;
  const def = data.defensiveZonePctg ?? 0;
  const total = off + neu + def;

  // Normaliser pour que la somme == 100 % (par précaution)
  const norm = total > 0 ? 100 / total : 1;
  const offPct = (off * norm);
  const neuPct = (neu * norm);
  const defPct = (def * norm);

  const percentile = rankToPercentile(data.rank, data.total);
  const colorClass = percentileToColorClass(percentile);

  return (
    <div
      className="h-100 rounded-3 p-3"
      style={{
        background: 'var(--card-bg)',
        border: '1px solid rgba(99,102,241,0.2)',
        borderTop: '3px solid #6366f1',
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <span className="text-muted small fw-semibold text-uppercase">
            {translate('nhl.edge.zone.time.title', language)}
          </span>
        </div>
        {percentile != null && (
          <span className={`small fw-bold ${colorClass}`}>
            {translate('nhl.edge.percentile', language)} {percentile}%
          </span>
        )}
      </div>

      {/* Barre de temps en zone empilée */}
      <div
        className="d-flex rounded-2 overflow-hidden mb-3"
        style={{ height: 28, gap: 2 }}
        role="img"
        aria-label={`Zone time — Offensive ${formatZonePercentage(off)} / Neutral ${formatZonePercentage(neu)} / Defensive ${formatZonePercentage(def)}`}
      >
        {offPct > 0 && (
          <div
            style={{
              width: `${offPct}%`,
              background: 'linear-gradient(90deg, #10b981, #34d399)',
              borderRadius: '4px 0 0 4px',
            }}
            title={`${translate('nhl.edge.zone.time.offensive', language)}: ${formatZonePercentage(off)}`}
          />
        )}
        {neuPct > 0 && (
          <div
            style={{
              width: `${neuPct}%`,
              background: 'linear-gradient(90deg, #64748b, #94a3b8)',
            }}
            title={`${translate('nhl.edge.zone.time.neutral', language)}: ${formatZonePercentage(neu)}`}
          />
        )}
        {defPct > 0 && (
          <div
            style={{
              width: `${defPct}%`,
              background: 'linear-gradient(90deg, #f43f5e, #fb7185)',
              borderRadius: '0 4px 4px 0',
            }}
            title={`${translate('nhl.edge.zone.time.defensive', language)}: ${formatZonePercentage(def)}`}
          />
        )}
      </div>

      {/* Légende */}
      <div className="d-flex flex-column gap-2">
        <ZoneLegendRow
          color="#10b981"
          label={translate('nhl.edge.zone.time.offensive', language)}
          pct={off}
        />
        <ZoneLegendRow
          color="#64748b"
          label={translate('nhl.edge.zone.time.neutral', language)}
          pct={neu}
        />
        <ZoneLegendRow
          color="#f43f5e"
          label={translate('nhl.edge.zone.time.defensive', language)}
          pct={def}
        />
      </div>

      {percentile != null && (
        <div className="mt-3 small text-muted">
          {translate('nhl.edge.percentile', language)}{' '}
          <span className={`fw-bold ${colorClass}`}>{percentile}</span>
        </div>
      )}
    </div>
  );
}

function ZoneLegendRow({
  color,
  label,
  pct,
}: {
  color: string;
  label: string;
  pct: number;
}) {
  return (
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="d-flex align-items-center gap-2">
        <div
          className="rounded-circle flex-shrink-0"
          style={{ width: 10, height: 10, background: color }}
        />
        <span className="small text-muted">{label}</span>
      </div>
      <span className="small fw-semibold">{formatZonePercentage(pct)}</span>
    </div>
  );
}

function formatZonePercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Carte de tirs par zone de danger
// ---------------------------------------------------------------------------

type SogData = NonNullable<NhlEdgeSkaterDetail['sogSummary']>['playerSeason'] & object;

function ShotZoneCard({ data, language }: { data: SogData; language: Language }) {
  const hdSog = data.hdSog ?? 0;
  const mdSog = data.mdSog ?? 0;
  const ldSog = data.ldSog ?? 0;
  const totalSog = hdSog + mdSog + ldSog;

  const percentile = rankToPercentile(data.rank, data.total);
  const colorClass = percentileToColorClass(percentile);

  return (
    <div
      className="h-100 rounded-3 p-3"
      style={{
        background: 'var(--card-bg)',
        border: '1px solid rgba(245,158,11,0.2)',
        borderTop: '3px solid #f59e0b',
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <span className="text-muted small fw-semibold text-uppercase">
            {translate('nhl.edge.shot.location.title', language)}
          </span>
        </div>
        {percentile != null && (
          <span className={`small fw-bold ${colorClass}`}>
            {translate('nhl.edge.percentile', language)} {percentile}%
          </span>
        )}
      </div>

      <div className="d-flex flex-column gap-3">
        <ShotZoneRow
          label={translate('nhl.edge.shot.location.high.danger', language)}
          sog={hdSog}
          goals={data.hdGoals}
          total={totalSog}
          color="#ef4444"
        />
        <ShotZoneRow
          label={translate('nhl.edge.shot.location.mid.range', language)}
          sog={mdSog}
          goals={data.mdGoals}
          total={totalSog}
          color="#f59e0b"
        />
        <ShotZoneRow
          label={translate('nhl.edge.shot.location.long.range', language)}
          sog={ldSog}
          goals={data.ldGoals}
          total={totalSog}
          color="#6366f1"
        />
      </div>

      {totalSog > 0 && (
        <div className="mt-3 pt-2 small text-muted" style={{ borderTop: '1px solid var(--border-color)' }}>
          {translate('nhl.edge.shot.location.sog', language)} total :{' '}
          <strong>{totalSog}</strong>
        </div>
      )}
    </div>
  );
}

function ShotZoneRow({
  label,
  sog,
  goals,
  total,
  color,
}: {
  label: string;
  sog: number;
  goals?: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (sog / total) * 100 : 0;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="d-flex align-items-center gap-2">
          <div
            className="rounded-circle flex-shrink-0"
            style={{ width: 8, height: 8, background: color }}
          />
          <span className="small fw-semibold">{label}</span>
        </div>
        <div className="d-flex gap-2 small text-muted">
          <span>{sog} TB</span>
          {goals != null && <span>· {goals} buts</span>}
        </div>
      </div>
      <div
        className="rounded-pill overflow-hidden"
        style={{ height: 5, background: 'var(--bs-gray-700, rgba(148,163,184,0.15))' }}
      >
        <div
          className="h-100 rounded-pill"
          style={{
            width: `${pct}%`,
            background: color,
            transition: 'width 0.6s ease',
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Carte de métrique gardien
// ---------------------------------------------------------------------------

interface GoalieMetricCardProps {
  title: string;
  value: string;
  rank?: number;
  total?: number;
  language: Language;
  accentColor: string;
  lowerIsBetter?: boolean;
}

function GoalieMetricCard({
  title,
  value,
  rank,
  total,
  language,
  accentColor,
  lowerIsBetter = false,
}: GoalieMetricCardProps) {
  const percentile = rankToPercentile(rank, total);
  // Pour les métriques où un rang bas est meilleur (ex: GAA), inverser le percentile
  const displayPercentile =
    lowerIsBetter && percentile != null ? 100 - percentile : percentile;
  const colorClass = percentileToColorClass(displayPercentile);

  return (
    <div
      className="h-100 rounded-3 p-3"
      style={{
        background: 'var(--card-bg)',
        border: `1px solid ${accentColor}33`,
        borderTop: `3px solid ${accentColor}`,
      }}
    >
      <div className="mb-3">
        <span className="text-muted small fw-semibold text-uppercase">{title}</span>
      </div>

      <div className="display-6 fw-bold mb-3">{value}</div>

      {rank != null && total != null && (
        <RankIndicator
          percentile={displayPercentile}
          colorClass={colorClass}
          language={language}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tableau de tirs par zone (gardien)
// ---------------------------------------------------------------------------

function GoalieShotLocationCard({
  data,
  language,
}: {
  data: NonNullable<NhlEdgeGoalieDetail['shotLocation']>['details'];
  language: Language;
}) {
  if (!data || data.length === 0) return null;

  return (
    <div
      className="rounded-3 p-3"
      style={{
        background: 'var(--card-bg)',
        border: '1px solid rgba(99,102,241,0.2)',
        borderTop: '3px solid #6366f1',
      }}
    >
      <div className="mb-3">
        <span className="text-muted small fw-semibold text-uppercase">
          {translate('nhl.edge.goalie.shot.location.title', language)}
        </span>
      </div>

      <div className="table-responsive">
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              <th className="text-muted small">{translate('nhl.edge.shot.location.high.danger', language).replace('High ', '').replace('Haute ', '')}</th>
              <th className="text-end text-muted small">{translate('nhl.edge.goalie.saves', language)}</th>
              <th className="text-end text-muted small">{translate('nhl.edge.goalie.goals.against', language)}</th>
              <th className="text-end text-muted small">{translate('nhl.edge.goalie.save.pctg', language)}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i}>
                <td className="small fw-semibold">{row.category ?? '—'}</td>
                <td className="text-end small">{row.saves ?? '—'}</td>
                <td className="text-end small">{row.goalsAgainst ?? '—'}</td>
                <td className="text-end small">
                  {row.savePctg != null
                    ? row.savePctg.toLocaleString(undefined, {
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      })
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utilitaire de formatage
// ---------------------------------------------------------------------------

function formatGoalieValue(value?: number, decimals = 2): string {
  if (value == null) return '—';
  return value.toFixed(decimals);
}
